import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";

import { usePromise, useSWRHooksConfig } from "@touchtech/web-hooks";

import { COLLECTION_SEARCH_PAGE_SIZE } from "@is/constants";
import {
  api,
  createCollectionByIdAddItemUrl,
  createCollectionByIdRemoveItemUrl,
  createCollectionByIdUrl,
  createCollectionCheckoutSuggestionsUrl,
  createCollectionDemoUrl,
  createCollectionStylesByIdUrl,
} from "@is/services/api";
import { Collection, CollectionStylesResponse } from "@is/types";

async function handleUpdateCollection(collection: Collection) {
  const url = createCollectionByIdUrl(collection.id);
  const data = await api.put(url, collection);

  return data;
}

async function handleStylesGet(
  collectionId: string,
  term: string,
  includeOutOfStockItems: boolean,
  page = 1
) {
  const url = createCollectionStylesByIdUrl(collectionId);

  const data = (await api.post(url, {
    terms: [term],
    page: page,
    pageSize: COLLECTION_SEARCH_PAGE_SIZE,
    includeOutOfStockItems,
  })) as CollectionStylesResponse;

  return data;
}

async function handleCollectionAddItemStyles(
  collectionId: string,
  styleId: string,
  variantId: string
) {
  const url = createCollectionByIdAddItemUrl(collectionId);

  const data = await api.patch(url, {
    styleId,
    variantId,
  });

  return data;
}

async function handleRemoveStyleFromCollection(
  collectionId: string,
  styleIndex: number
) {
  const url = createCollectionByIdRemoveItemUrl(collectionId, styleIndex);

  const data = await api.delete(url);

  return data;
}

async function handleUpdateCollectionDemo(
  collectionId: string,
  isDemoCollection: boolean
) {
  const url = createCollectionDemoUrl(collectionId);

  const data = await api.patch(url, { isDemoCollection });

  return data;
}

async function handleUpdateCollectionCheckoutSuggestions(
  collectionId: string,
  isCheckoutSuggestionsCollection: boolean
) {
  const url = createCollectionCheckoutSuggestionsUrl(collectionId);

  const data = await api.patch(url, { isCheckoutSuggestionsCollection });

  return data;
}

export const useCollection = (collectionId: string) => {
  const url = createCollectionByIdUrl(collectionId);

  const { data, error, isLoading } = useSWR(!!collectionId && url, api.get);

  const [collection, setCollection] = useState<Collection>(null);

  const [{ isPending }, dispatch] = usePromise();
  const swrConfig = useSWRHooksConfig();

  useEffect(() => {
    setCollection(data as Collection);
  }, [data]);

  const updateStateAndMutate = useCallback(
    (newCollection: Collection) => {
      setCollection(newCollection);
      swrConfig.mutate(url, newCollection, false);
    },
    [swrConfig, url]
  );

  const updateCollection = useCallback(
    async (updatedCollection: Partial<Collection>) => {
      const newCollection = (await dispatch(
        handleUpdateCollection({
          ...collection,
          ...updatedCollection,
        })
      )) as Collection;

      if (newCollection) {
        updateStateAndMutate(newCollection);
      }

      return newCollection;
    },
    [dispatch, collection, updateStateAndMutate]
  );

  const getStyles = useCallback(
    async (term: string, includeOutOfStockItems: boolean, page = 1) => {
      const styles = (await dispatch(
        handleStylesGet(collectionId, term, includeOutOfStockItems, page)
      )) as CollectionStylesResponse;

      return styles;
    },
    [dispatch, collectionId]
  );

  const addStyleToCollection = useCallback(
    async (styleId: string, variantId: string) => {
      const newCollection = (await dispatch(
        handleCollectionAddItemStyles(collectionId, styleId, variantId)
      )) as Collection;

      if (newCollection) {
        updateStateAndMutate(newCollection);
      }

      return newCollection;
    },
    [dispatch, collectionId, updateStateAndMutate]
  );

  const deleteStyleFromCollection = useCallback(
    async (index: number) => {
      const newCollection = (await dispatch(
        handleRemoveStyleFromCollection(collectionId, index)
      )) as Collection;

      if (newCollection) {
        updateStateAndMutate(newCollection);
      }

      return newCollection;
    },
    [dispatch, collectionId, updateStateAndMutate]
  );

  const updateCollectionDemo = useCallback(
    async (isDemoCollection: boolean) => {
      const newCollection = (await dispatch(
        handleUpdateCollectionDemo(collectionId, isDemoCollection)
      )) as Collection;

      if (newCollection) {
        updateStateAndMutate(newCollection);
      }

      return newCollection;
    },
    [dispatch, collectionId, updateStateAndMutate]
  );

  const updateCollectionCheckoutSuggestions = useCallback(
    async (isCheckoutSuggestionsCollection: boolean) => {
      const newCollection = (await dispatch(
        handleUpdateCollectionCheckoutSuggestions(
          collectionId,
          isCheckoutSuggestionsCollection
        )
      )) as Collection;

      if (newCollection) {
        updateStateAndMutate(newCollection);
      }

      return newCollection;
    },
    [dispatch, collectionId, updateStateAndMutate]
  );

  return {
    collection,
    isLoading,
    isPending,
    error,
    updateCollection,
    getStyles,
    addStyleToCollection,
    deleteStyleFromCollection,
    updateCollectionDemo,
    updateCollectionCheckoutSuggestions,
  };
};
